import { useState, useEffect, useContext, useCallback } from "react";
import { Modal, Button, Spinner, Card } from "react-bootstrap";
import axios from "axios";
import { AuthContext } from "../AuthContext";
import { jwtDecode } from "jwt-decode";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import "../Components/design/Bitacora.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";


// Función para decodificar el JWT y obtener el ID del usuario
const getUserIdFromToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    console.log("User ID from JWT:", decoded.id || decoded.user_id || decoded._id);
    return decoded.id || decoded.user_id || decoded._id || null;
  } catch {
    return null;
  }
};

const formatDate = (date) => date.toISOString().split("T")[0];

const Bitacora = () => {
  const { user } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Estados para agregar nuevas entradas
  const [showModal, setShowModal] = useState(false);
  const [newEntry, setNewEntry] = useState({ fecha: formatDate(new Date()), titulo: "", texto: "" });

  // Estados para editar entradas
  const [editingEntry, setEditingEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [currentWeek, setCurrentWeek] = useState(new Date());


  const userId = user?.token ? getUserIdFromToken(user.token) : null;

  const getStartOfWeek = (date) => {
    const temp = new Date(date);
    const diff = temp.getDay() === 0 ? -6 : 1 - temp.getDay();
    temp.setDate(temp.getDate() + diff);
    return temp;
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(getStartOfWeek(currentWeek));
    day.setDate(day.getDate() + i);
    return {
      date: formatDate(day),
      label: day.toLocaleDateString("es-ES", { weekday: "long", day: "2-digit" }),
    };
  });

  // const entriesByDay = weekDays.reduce((acc, day) => {
  //   const entryDate = formatDate(new Date(day.date));

  //   // filtrar entradas solo de ese día
  //   const dayEntries = entries.filter(
  //     (entry) => formatDate(new Date(entry.fecha)) === entryDate
  //   );

  //   acc[day.label] = dayEntries; // 👈 usas el label tipo "L 18"
  //   console.log(`Entradas para ${day.label}:`, dayEntries);
  //   if (dayEntries.length === 0) {
  //     acc[day.label] = [{ titulo: "Sin entradas", texto: "", fecha: day.date }];
  //   }
  //   return acc;
  // }, {});

  const fetchEntries = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await axios.get(`https://socios.kolpingchile.cl/backend/api/bitacoras/${userId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
        timeout: 1000000, // 1000 segundos
      });
      setEntries(res.data);
    } catch (err) {
      console.error("Error al obtener bitácoras:", err);
      setError("No se pudieron cargar las entradas.");
    } finally {
      setLoading(false);
    }
  }, [userId, user?.token]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries, currentWeek]);

  // Maneja los cambios de los campos en el formulario (para agregar o editar)
  const handleInputChange = (e, isEdit = false) => {
    if (isEdit) {
      setEditingEntry((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    } else {
      setNewEntry((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

  // Envía el formulario para agregar una nueva entrada
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newEntry.fecha || !newEntry.titulo || !newEntry.texto) {
      return alert("Todos los campos son obligatorios");
    }
    try {
      const entryData = { ...newEntry, user_id: userId };
      const res = await axios.post("https://socios.kolpingchile.cl/backend/api/bitacoras", entryData, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setEntries((prev) => [...prev, res.data]);
      fetchEntries();
      setShowModal(false);
      setNewEntry({ fecha: formatDate(new Date()), titulo: "", texto: "" });
    } catch (err) {
      console.error("Error al guardar entrada:", err);
      alert("No se pudo guardar la entrada.");
    }
  };

  // Abre el modal para editar la entrada y pre-carga los datos
  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowEditModal(true);
  };

  // Envía la actualización de la entrada al backend
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editingEntry.fecha || !editingEntry.titulo || !editingEntry.texto) {
      return toast.error("Todos los campos son obligatorios para editar");
    }

    try {
      await axios.put(
        `https://socios.kolpingchile.cl/backend/api/bitacoras/${editingEntry.id || editingEntry._id}`,
        editingEntry,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
      window.location.reload();

      toast.success("Entrada actualizada con éxito");

      setShowEditModal(false);
      setEditingEntry(null);

      await fetchEntries();


    } catch (err) {
      console.error("Error al editar entrada:", err);
      toast.error("No se pudo editar la entrada.");
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar esta entrada?")) return;

    try {
      await axios.delete(`https://socios.kolpingchile.cl/backend/api/bitacoras/${id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      await fetchEntries(); // Refrescar lista
    } catch (err) {
      console.error("Error al eliminar entrada:", err);
      alert("No se pudo eliminar la entrada.");
    }
  };

  // Función para exportar las entradas a PDF
  const exportPDF = () => {
    const input = document.querySelector(".bitacora-section"); // 👈 el div principal
    if (!input) return;

    // Ocultar botones (con clase no-export)
    const hiddenEls = input.querySelectorAll(".no-export");
    hiddenEls.forEach(el => (el.style.display = "none"));

    html2canvas(input, {
      scale: 3, // aumenta la calidad
      useCORS: true,
      scrollX: 0,
      scrollY: -window.scrollY, // corrige si hay scroll
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Escalar imagen para que se ajuste dentro de una hoja
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      if (imgHeight > pdfHeight) {
        imgHeight = pdfHeight;
        imgWidth = (canvas.width * pdfHeight) / canvas.height;
      }

      const posX = (pdfWidth - imgWidth) / 2; // centrar horizontal
      const posY = (pdfHeight - imgHeight) / 2; // centrar vertical

      pdf.addImage(imgData, "PNG", posX, posY, imgWidth, imgHeight);
      pdf.save("bitacora.pdf");

      // Restaurar visibilidad
      hiddenEls.forEach(el => (el.style.display = ""));
    });
  };



  // Filtra las entradas para un día específico (compara solo la parte de la fecha)
  const filteredEntries = (date) =>
    entries.filter((entry) => entry.fecha?.split("T")[0] === date);

  return (

    <div className="container mt-4">
      <Card className='shadow p-4' style={{ backgroundColor: "#f8f9fa" }}>
        <Card.Body>
          <div className="container mt-4">
            {/* Modal para agregar nueva entrada */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
              <Modal.Header closeButton style={{ backgroundColor: "#F28C28", color: "#fff" }}>
                <Modal.Title>Agregar Entrada</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form onSubmit={handleSubmit}>
                  <input type="date" name="fecha" className="form-control mb-2" value={newEntry.fecha} onChange={(e) => handleInputChange(e)} required />
                  <input type="text" name="titulo" className="form-control mb-2" placeholder="Título" value={newEntry.titulo} onChange={(e) => handleInputChange(e)} required />
                  <textarea name="texto" className="form-control mb-2" rows="3" placeholder="Descripción" value={newEntry.texto} onChange={(e) => handleInputChange(e)} required />
                  <Button variant="dark" type="submit" className="w-100">Agregar</Button>
                </form>
              </Modal.Body>
            </Modal>

            {/* Modal para editar entrada */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
              <Modal.Header closeButton style={{ backgroundColor: "#F28C28", color: "#fff" }}>
                <Modal.Title>Editar Entrada</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <form onSubmit={handleEditSubmit}>
                  <input
                    type="date"
                    name="fecha"
                    className="form-control mb-2"
                    value={editingEntry?.fecha ? editingEntry.fecha.split("T")[0] : ""}
                    onChange={(e) => handleInputChange(e, true)}
                    required
                  />
                  <input
                    type="text"
                    name="titulo"
                    className="form-control mb-2"
                    placeholder="Título"
                    value={editingEntry?.titulo || ""}
                    onChange={(e) => handleInputChange(e, true)}
                    required
                  />
                  <textarea
                    name="texto"
                    className="form-control mb-2"
                    rows="3"
                    placeholder="Descripción"
                    value={editingEntry?.texto || ""}
                    onChange={(e) => handleInputChange(e, true)}
                    required
                  />
                  <Button variant="dark" type="submit" className="w-100">Guardar Cambios</Button>
                </form>
              </Modal.Body>
            </Modal>
            <div className="bitacora-section">
              {/* Controles de semana */}
              <div className="d-flex justify-content-between mb-3">
                <Button
                  className="no-export"
                  variant="dark"
                  onClick={() =>
                    setCurrentWeek(prev => {
                      const newDate = new Date(prev); // Copia la fecha actual
                      newDate.setDate(newDate.getDate() - 7); // Resta 7 días
                      return newDate;
                    })
                  }
                >
                  ← Semana Anterior
                </Button>

                <Button
                  className="no-export"
                  variant="dark"
                  onClick={() =>
                    setCurrentWeek(prev => {
                      const newDate = new Date(prev); // Copia la fecha actual
                      newDate.setDate(newDate.getDate() + 7); // Suma 7 días
                      return newDate;
                    })
                  }
                >
                  Semana Siguiente →
                </Button>
              </div>

              <h4 className="text-center mb-4" style={{ color: "#F28C28" }}>
                SEMANA DE {getStartOfWeek(currentWeek).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }).toUpperCase()}
              </h4>

              {/* Tabla de días y entradas */}
              {/* Fila 1: Lunes a Jueves */}
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 mb-3">
                {weekDays.slice(0, 4).map((day) => (
                  <div className="col" key={day.date}>
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th scope="col" className="text-capitalize" style={{ color: "#F28C28" }}>{day.label}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {filteredEntries(day.date).map((entry) => (
                              <div
                                key={entry.id || entry._id}
                                className="mb-2 border rounded p-2"
                                style={{ backgroundColor: "#fff", color: "#000" }}
                              >
                                <strong >{entry.titulo.toUpperCase()}</strong>
                                <p className="mb-2" style={{ color: "#777572ff" }}>{entry.texto}</p>
                                <div className="d-flex gap-2 justify-content-center">
                                  <Button
                                    className="no-export"
                                    size="sm"
                                    variant="dark"
                                    onClick={() => handleEdit(entry)}
                                  >
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                  </Button>
                                  <Button
                                    className="no-export"
                                    size="sm"
                                    variant="danger"
                                    onClick={() => handleDelete(entry.id || entry._id)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </div>
                              </div>
                            ))}
                            <Button
                              className="no-export"
                              size="sm"
                              style={{ backgroundColor: "#F28C28", color: "white", border: "none" }}
                              onClick={() => {
                                setNewEntry((prev) => ({ ...prev, fecha: day.date }));
                                setShowModal(true);
                              }}
                            >
                              + Agregar Entrada
                            </Button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>

              {/* Fila 2: Viernes a Domingo */}
              <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 mb-3">
                {weekDays.slice(4, 7).map((day) => (
                  <div className="col" key={day.date}>
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th scope="col" className="text-capitalize" style={{ color: "#F28C28" }}>{day.label}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {filteredEntries(day.date).map((entry) => (
                              <div
                                key={entry.id || entry._id}
                                className="mb-2 border rounded p-2"
                                style={{ backgroundColor: "#fff", color: "#000" }}
                              >
                                <strong >{entry.titulo.toUpperCase()}</strong>
                                <p className="mb-2" style={{ color: "#777572ff" }}>{entry.texto}</p>
                                <div className="d-flex gap-2 justify-content-center">
                                  <Button
                                    className="no-export"
                                    size="sm"
                                    variant="dark"
                                    onClick={() => handleEdit(entry)}
                                  >
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                  </Button>
                                  <Button
                                    className="no-export"
                                    size="sm"
                                    variant="danger"
                                    onClick={() => handleDelete(entry.id || entry._id)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </div>
                              </div>
                            ))}
                            <Button
                              className="no-export"
                              size="sm"
                              style={{ backgroundColor: "#F28C28", color: "white", border: "none" }}
                              onClick={() => {
                                setNewEntry((prev) => ({ ...prev, fecha: day.date }));
                                setShowModal(true);
                              }}
                            >
                              + Agregar Entrada
                            </Button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                ))}
                <div className="text-center mb-3">
                  <Button className="no-export" size="lg" variant="danger" onClick={() => exportPDF()}>
                    <FontAwesomeIcon icon={faFilePdf} />
                  </Button>
                </div>
              </div>
            </div>

            {loading && <div className="text-center"><Spinner animation="border" variant="warning" /></div>}
            {error && <p className="text-danger text-center">{error}</p>}
          </div>
        </Card.Body>

      </Card>

    </div>
  );
};

<ToastContainer position="bottom-right" autoClose={3000} />

export default Bitacora;
